import React from 'react';
import StatusPill from './StatusPill';

/**
 * Fat, tappable list row for the staff (shop-floor) view — used for parts,
 * services, tasks, and history items. ≥ 56px tall, big touch target.
 *
 * Layout: [ leading StatusPill / icon ] [ label + optional sublabel ] [ trailing action ]
 *
 * Props:
 *   status       — domain status string → renders a leading <StatusPill> (mapped)
 *   leading      — custom leading node (overrides `status` pill if provided)
 *   label        — primary text (already localized)
 *   sublabel     — optional secondary text
 *   onClick      — row tap handler
 *   trailingLabel/trailingIcon/onTrailingClick — optional trailing action button.
 *                  Its click STOPS PROPAGATION so it never triggers the row tap.
 *   trailingVariant — 'success' (default) | 'danger' | 'neutral'
 *   disabled     — disables row interaction styling
 */
const TRAILING_VARIANTS = {
  success: 'bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-400',
  danger: 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400',
  neutral: 'bg-surface-3 text-content hover:bg-surface-2',
};

const BigRow = ({
  status,
  leading,
  label,
  sublabel,
  onClick,
  trailingLabel,
  trailingIcon: TrailingIcon,
  onTrailingClick,
  trailingVariant = 'success',
  disabled = false,
  className = '',
}) => {
  const interactive = typeof onClick === 'function' && !disabled;

  const handleKeyDown = (e) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  };

  const handleTrailing = (e) => {
    // Critical: never let the trailing action bubble up and trigger the row tap.
    e.stopPropagation();
    if (onTrailingClick) onTrailingClick(e);
  };

  const showTrailing = trailingLabel || TrailingIcon;
  const trailingClass = TRAILING_VARIANTS[trailingVariant] || TRAILING_VARIANTS.success;

  return (
    <div
      data-testid="big-row"
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={handleKeyDown}
      className={`min-h-[56px] w-full flex items-center gap-3 p-3 rounded-xl bg-surface border border-edge ${
        interactive ? 'cursor-pointer hover:bg-surface-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring' : ''
      } ${className}`}
    >
      {/* Leading status / icon */}
      <div className="shrink-0">
        {leading !== undefined ? leading : <StatusPill status={status} size="sm" showLabel={false} />}
      </div>

      {/* Label + sublabel */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-content truncate">{label}</div>
        {sublabel && <div className="text-xs text-content-muted truncate">{sublabel}</div>}
      </div>

      {/* Trailing action */}
      {showTrailing && (
        <button
          type="button"
          onClick={handleTrailing}
          disabled={disabled}
          className={`shrink-0 min-h-[40px] px-3 inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${trailingClass}`}
        >
          {TrailingIcon && <TrailingIcon size={16} className="shrink-0" />}
          {trailingLabel && <span>{trailingLabel}</span>}
        </button>
      )}
    </div>
  );
};

export default BigRow;
